import {useCallback, useState} from 'react';
import NextLink from 'next/link';
import pluralize from 'pluralize';

import {LabResultMetricTypeEnum} from '#apollo/schema';
import {AnimatedText, Button, Icon, NumberTicker, Rings, Text} from '#component/base';
import {KitRegisteredPill} from '#component/partial';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {ShareTrigger} from '#component/util';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {getFormattedAgeAsString} from '#util/tallyAge';

import {TallyAgeHeaderProps} from './TallyAgeHeader.types';

import * as css from './TallyAgeHeader.css';

const getRingsVariantForDeltaAge = (deltaAge = 0) => {
  if (deltaAge === 0) {
    return 'double';
  }

  return deltaAge > 0 ? 'grow' : 'shrink';
};

export const TallyAgeHeader = ({
  deltaAge = 0,
  formattedBioAge,
  formattedChronologicalAge,
  formattedDeltaAge,
  labResultMetrics = [],
  labResultReportUrl,
  registeredDate,
}: TallyAgeHeaderProps) => {
  const {track} = useAnalytics();
  const {isMobile, isDesktop} = useViewportSize();
  const [isDownloading, setIsDownloading] = useState(false);
  const {years: deltaAgeYears = 0, months: deltaAgeMonths = 0} = formattedDeltaAge || {};
  const {years: bioAgeYears = 0, months: bioAgeMonths = 0} = formattedBioAge || {};
  const {years: chronoAgeYears = 0} = formattedChronologicalAge || {};
  const isEqual = deltaAge === 0;
  const isYounger = deltaAge < 0;
  const percentile = parseFloat(
    (
      labResultMetrics?.find(({type}) => type === LabResultMetricTypeEnum.TallyGeneralAgePercentile)
        ?.percentile || 0
    ).toFixed(1),
  );

  // While we don't know exactly how long it will take to download the report,
  // we can use a timeout to simulate the download process
  // and give the user visual feedback that the download is happening
  const handleDownloadLabResultReport = useCallback(async () => {
    setIsDownloading(true);
    try {
      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Lab Results Report Downloaded',
      }).catch();
      await new Promise(resolve => setTimeout(resolve, 3000));
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const renderedRings = (
    <div className={sprinkles({items: 'start', pos: 'relative', w: 'maxContent', z: 0})}>
      <Rings className={css.rings} variant={getRingsVariantForDeltaAge(deltaAge)} />

      <div className={css.ageContainer}>
        <NumberTicker
          appear
          className={css.age}
          color="white"
          fontWeight="ultraLight"
          initialValue={chronoAgeYears}>
          {bioAgeYears}
        </NumberTicker>

        <div className={css.ageSubtext}>
          <Icon
            name={isEqual ? 'ArrowsInSimple' : isYounger ? 'TrendDown' : 'TrendUp'}
            color="peach"
          />
          <Text color="white" variant="bodyLarge">
            {pluralize('year', bioAgeYears)}
            {bioAgeMonths > 0 && (
              <>
                {' & '}
                <br />
                {pluralize('month', bioAgeMonths, true)}
              </>
            )}
          </Text>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={sprinkles({
          colSpan: [4, 3],
          colStart: [1, 2],
          flex: ['flex', 'flex', 'none'],
          items: 'start',
          w: 'full',
        })}>
        {renderedRings}
      </div>

      <div
        className={sprinkles({
          colSpan: [4, 6, 5],
          colStart: [1, 2],
          flex: 'flex',
          dir: 'col',
          gap: [24, 32, 48],
          pos: 'relative',
          z: 1,
        })}>
        {registeredDate && (
          <KitRegisteredPill
            className={sprinkles({display: ['none', 'none', 'flex']})}
            registeredDate={registeredDate}
            variant="primaryDark">
            {' results'}
          </KitRegisteredPill>
        )}

        <p>
          <AnimatedText as="span" color="cotton" variant="heading2">
            Your TallyAge™ is
          </AnimatedText>

          <AnimatedText as="span" color="peach" delay={20} variant="heading2">
            {isEqual
              ? 'exactly the same'
              : `${getFormattedAgeAsString(deltaAgeYears, deltaAgeMonths)} ${
                  isYounger ? 'lower than' : 'higher than'
                }`}
          </AnimatedText>

          <AnimatedText as="span" color="cotton" delay={140} variant="heading2">
            {isEqual ? 'as your chronological age' : 'your chronological age'}
          </AnimatedText>
        </p>

        {registeredDate && (
          <KitRegisteredPill
            className={sprinkles({display: ['flex', 'flex', 'none']})}
            registeredDate={registeredDate}
            variant="primaryDark">
            {' results'}
          </KitRegisteredPill>
        )}

        <div className={sprinkles({flex: 'flex', gap: [12, 24], wrap: ['wrap', 'nowrap']})}>
          <NextLink href="/register" passHref>
            <Button
              as="a"
              className={sprinkles({
                display: ['none', 'flex'],
                width: 'maxContent',
                paddingLeft: 24,
                paddingRight: 24,
              })}
              variant="primary-dark">
              <Text color="inherit" variant={isMobile ? 'buttonSmall' : 'button'}>
                Register a new test
              </Text>
            </Button>
          </NextLink>

          <Button
            as="a"
            href={labResultReportUrl}
            aria-label="Download my results"
            icon="DownloadSimple"
            variant="secondary-dark"
            className={sprinkles({width: 'maxContent'})}
            loading={isDownloading}
            onClick={handleDownloadLabResultReport}>
            {isDesktop && <span aria-hidden>Download Report</span>}
          </Button>

          <ShareTrigger
            actionName="Request dash share URL"
            formattedDeltaAge={formattedDeltaAge}
            isYounger={isYounger}
            percentile={percentile}>
            {({onClick}) => (
              <Button
                aria-label="Share my results"
                icon="ShareNetwork"
                onClick={onClick}
                variant="secondary-dark"
                className={sprinkles({width: 'maxContent'})}
              />
            )}
          </ShareTrigger>

          <NextLink href="/results" passHref>
            <Button aria-label="View my results" as="a" icon="Play" variant="secondary-dark" />
          </NextLink>

          <NextLink href="/register" passHref>
            <Button
              as="a"
              className={sprinkles({
                display: ['flex', 'none'],
                width: 'maxContent',
                paddingLeft: [48, 72],
                paddingRight: [48, 72],
              })}
              variant="primary-dark">
              <Text color="inherit" variant={isMobile ? 'buttonSmall' : 'button'}>
                Register a new test
              </Text>
            </Button>
          </NextLink>
        </div>
      </div>

      <div
        className={sprinkles({
          colSpan: 3,
          colStart: 8,
          flex: ['none', 'none', 'flex'],
          items: 'start',
          mL: 64,
          w: 'full',
        })}>
        {renderedRings}
      </div>
    </>
  );
};
